/* v8 Path AG — shared cell-class helpers for statement-style tables.
   FormalStatementTable grew these first (filing-style Income
   Statement / Balance Sheet / Cash Flow tables), and MetricHorizonTable
   re-invented similar patterns inline. Centralizing them here lets
   comparative tables (KPIs, ratios, custom horizons) inherit the
   same visual language as the 3-statement model without copy-paste.

   Three helpers:
     - labelCellCls({ isMulti, indent, rule })
         → left-most (sticky) label column
     - amtCellCls({ isMulti, rule, isDouble })
         → right-aligned amount cell
     - STICKY_LEFT_SHADOW
         → the right-edge scroll shadow shared by every sticky-left
           cell (tbody label cell, thead corner cell, H1/H2 banner
           label cells). Exported so consumers can compose it with
           bespoke cell styles without re-hardcoding the value.

   Why tokens live here instead of periodStyles.js: periodStyles is
   about the sticky rectangle mechanics (top-offset, z-indices, thead
   line). cellStyles is about the _content_ of a cell (padding,
   typography, indent). Two separable concerns, two modules. */

/* v8 Path T — tightened shadow. Larger blur radii (originally
   12px/15px) forced a bigger repaint area and caused noticeable
   scroll jank on mid-range laptops. 6px blur still visually masks
   amount cells transitioning in/out behind the sticky column. */
export const STICKY_LEFT_SHADOW = 'shadow-[6px_0_8px_-3px_rgba(0,0,0,0.45)]';

/* Sticky-left label cell background. `!bg-[#1c1c30]` is !important
   to beat the row-hover rule `hover:[&>td]:bg-white/[0.03]` on the
   parent <tr>. Without the !, hover makes the sticky label column
   semi-transparent and amount cells behind it bleed through. */
const STICKY_LEFT_LABEL_BG = '!bg-[#1c1c30]';

export function labelCellCls({ isMulti, indent, rule } = {}) {
  const parts = [];
  if (isMulti) {
    /* `border-l-0` — the cell's own left border was painted in
       `border-white/[0.06]` (translucent), which let scrolled-off
       amount cells' left borders show through as 1px vertical
       tick-marks on the table's left edge. The outer frame border
       handles the left edge; this cell doesn't need its own. */
    parts.push(
      `sticky left-0 z-[10] ${STICKY_LEFT_LABEL_BG} min-w-[150px] max-w-[200px] border border-l-0 border-white/[0.06] text-left px-2 py-1 ${STICKY_LEFT_SHADOW}`
    );
  } else {
    parts.push('py-[3px]');
  }
  if (rule) parts.push('border-t border-border !pt-[6px]');
  if (indent === 1) parts.push(isMulti ? '!pl-[18px]' : 'pl-[18px]');
  if (indent === 2) parts.push(isMulti ? '!pl-[36px]' : 'pl-[36px]');
  return parts.join(' ');
}

export function amtCellCls({ isMulti, rule, isDouble } = {}) {
  const parts = ['text-right font-mono whitespace-nowrap'];
  if (isMulti) parts.push('px-2 py-1 min-w-[110px] border border-white/[0.06] relative overflow-hidden');
  else parts.push('py-[3px]');
  if (rule) parts.push('border-t border-border !pt-[6px]');
  if (isDouble) parts.push('border-b-[3px] border-double border-current pb-[3px]');
  return parts.join(' ');
}
